// src/store/index.ts
import { createStore } from "zustand/vanilla";
import { createJSONStorage, persist } from "zustand/middleware";

// src/store/globalSlice.ts
import { produce } from "immer";

// src/utils/ids.ts
function createId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const rand = Math.random().toString(36).slice(2, 10);
  const time = Date.now().toString(36);
  return `id_${time}_${rand}`;
}

// src/store/globalSlice.ts
function removeFromArray(arr, predicate) {
  const out = [];
  for (const it of arr) if (!predicate(it)) out.push(it);
  return out;
}
function unique(arr) {
  return Array.from(new Set(arr));
}
function rebuildOpenTabs(state) {
  const ids = [
    ...state.ungroupedTabIds || [],
    ...(state.tabGroups || []).flatMap((g) => g.tabIds || [])
  ];
  const titleById = {};
  for (const t of state.openTabs || []) titleById[t.id] = t.title;
  for (const c of state.conversations || []) {
    const t = c.title || c.first_user_message_preview || "New Chat";
    if (!titleById[c.id]) titleById[c.id] = t;
  }
  state.openTabs = ids.map((id) => ({ id, title: titleById[id] || "New Chat" }));
}
var createGlobalSlice = (set, get) => ({
  config: {
    agentId: null,
    apiBaseUrl: "",
    authMode: "WIDGET",
    compatApiFallbackEnabled: true,
    interactionMode: null,
    workspaceMode: null,
    flowStep: null,
    readinessSummary: null,
    persistNamespace: void 0,
    features: {
      threadsEnabled: false,
      commentsEnabled: false,
      attachmentsEnabled: true,
      historyEnabled: true,
      tabsEnabled: true
    }
  },
  runOverrides: {},
  activeConversationId: null,
  conversations: [],
  isSidebarCollapsed: false,
  openTabs: [],
  tabGroups: [],
  ungroupedTabIds: [],
  setActiveConversationId: (id) => {
    if (id) {
      const st = get();
      if (!st.sessions[id]) {
        st.initSession(id);
      }
    }
    set({ activeConversationId: id });
  },
  setConversations: (list) => set({ conversations: list }),
  updateConversationMeta: (id, updates) => set(
    produce((state) => {
      const idx = state.conversations.findIndex((c) => c.id === id);
      if (idx !== -1) state.conversations[idx] = { ...state.conversations[idx], ...updates };
      const tab = state.openTabs.find((t) => t.id === id);
      if (tab && updates.title) tab.title = updates.title;
    })
  ),
  setSidebarCollapsed: (v) => set({ isSidebarCollapsed: v }),
  setOpenTabs: (tabs) => set({ openTabs: tabs }),
  setRunOverrides: (o) => set(
    produce((state) => {
      state.runOverrides = { ...state.runOverrides, ...o };
    })
  ),
  // M3: Reorder flat tabs (used by DnD fallback)
  moveTabWithinFlat: (oldIndex, newIndex) => set(
    produce((state) => {
      const arr = state.openTabs || [];
      if (oldIndex < 0 || newIndex < 0 || oldIndex >= arr.length || newIndex >= arr.length) return;
      const [moved] = arr.splice(oldIndex, 1);
      arr.splice(newIndex, 0, moved);
      state.openTabs = arr;
      if ((state.tabGroups?.length || 0) === 0) {
        state.ungroupedTabIds = arr.map((t) => t.id);
      }
    })
  ),
  // M4: Group actions
  migrateOpenTabsToGroups: () => set(
    produce((state) => {
      if ((state.tabGroups?.length || 0) > 0 || (state.ungroupedTabIds?.length || 0) > 0) return;
      state.ungroupedTabIds = (state.openTabs || []).map((t) => t.id);
    })
  ),
  createTabGroup: (name, color) => {
    const id = createId();
    set(
      produce((state) => {
        const g = { id, name: name || "Group", color: color || "blue", collapsed: false, tabIds: [] };
        state.tabGroups = [...state.tabGroups || [], g];
        rebuildOpenTabs(state);
      })
    );
    return id;
  },
  renameTabGroup: (groupId, name) => set(
    produce((state) => {
      const g = (state.tabGroups || []).find((x) => x.id === groupId);
      if (g) g.name = (name || "Group").trim() || "Group";
    })
  ),
  setTabGroupColor: (groupId, color) => set(
    produce((state) => {
      const g = (state.tabGroups || []).find((x) => x.id === groupId);
      if (g) g.color = color || g.color || "blue";
    })
  ),
  setTabGroupCollapsed: (groupId, collapsed) => set(
    produce((state) => {
      const g = (state.tabGroups || []).find((x) => x.id === groupId);
      if (g) g.collapsed = !!collapsed;
    })
  ),
  reorderGroups: (groupIdsInOrder) => set(
    produce((state) => {
      const byId = new Map((state.tabGroups || []).map((g) => [g.id, g]));
      const next = [];
      groupIdsInOrder.forEach((id) => {
        const g = byId.get(id);
        if (g) next.push(g);
        byId.delete(id);
      });
      for (const g of byId.values()) next.push(g);
      state.tabGroups = next;
      rebuildOpenTabs(state);
    })
  ),
  moveTab: (tabId, targetGroupId, targetIndex) => set(
    produce((state) => {
      state.ungroupedTabIds = removeFromArray(state.ungroupedTabIds || [], (id) => id === tabId);
      (state.tabGroups || []).forEach((g) => {
        g.tabIds = removeFromArray(g.tabIds || [], (id) => id === tabId);
      });
      if (!targetGroupId) {
        const arr = state.ungroupedTabIds || [];
        const idx = Math.max(0, Math.min(targetIndex, arr.length));
        arr.splice(idx, 0, tabId);
        state.ungroupedTabIds = unique(arr);
      } else {
        const g = (state.tabGroups || []).find((x) => x.id === targetGroupId);
        if (g) {
          const arr = g.tabIds || [];
          const idx = Math.max(0, Math.min(targetIndex, arr.length));
          arr.splice(idx, 0, tabId);
          g.tabIds = unique(arr);
        } else {
          const arr = state.ungroupedTabIds || [];
          const idx = Math.max(0, Math.min(targetIndex, arr.length));
          arr.splice(idx, 0, tabId);
          state.ungroupedTabIds = unique(arr);
        }
      }
      rebuildOpenTabs(state);
    })
  ),
  deleteTabGroup: (groupId, strategy = "ungroup") => set(
    produce((state) => {
      const groups = state.tabGroups || [];
      const idx = groups.findIndex((g2) => g2.id === groupId);
      if (idx === -1) return;
      const g = groups[idx];
      if (strategy === "ungroup") {
        state.ungroupedTabIds = unique([...state.ungroupedTabIds || [], ...g.tabIds || []]);
      } else if (strategy === "deleteTabs") {
        const deleteSet = new Set(g.tabIds || []);
        state.openTabs = (state.openTabs || []).filter((t) => !deleteSet.has(t.id));
        if (state.activeConversationId && deleteSet.has(state.activeConversationId)) {
          state.activeConversationId = null;
        }
      }
      state.tabGroups = groups.filter((_g, i) => i !== idx);
      rebuildOpenTabs(state);
    })
  ),
  // M5: Closing helpers (group-aware)
  closeTab: (tabId) => set(
    produce((state) => {
      state.openTabs = (state.openTabs || []).filter((t) => t.id !== tabId);
      state.ungroupedTabIds = (state.ungroupedTabIds || []).filter((id) => id !== tabId);
      (state.tabGroups || []).forEach((g) => {
        g.tabIds = (g.tabIds || []).filter((id) => id !== tabId);
      });
    })
  ),
  closeOthers: (referenceTabId) => set(
    produce((state) => {
      const refIdx = (state.openTabs || []).findIndex((t) => t.id === referenceTabId);
      if (refIdx === -1) return;
      const keep = /* @__PURE__ */ new Set([referenceTabId]);
      state.openTabs = (state.openTabs || []).filter((t) => keep.has(t.id));
      state.ungroupedTabIds = (state.ungroupedTabIds || []).filter((id) => keep.has(id));
      (state.tabGroups || []).forEach((g) => {
        g.tabIds = (g.tabIds || []).filter((id) => keep.has(id));
      });
    })
  ),
  closeToRight: (referenceTabId) => set(
    produce((state) => {
      const arr = state.openTabs || [];
      const idx = arr.findIndex((t) => t.id === referenceTabId);
      if (idx === -1) return;
      const keep = new Set(arr.slice(0, idx + 1).map((t) => t.id));
      state.openTabs = arr.filter((t) => keep.has(t.id));
      state.ungroupedTabIds = (state.ungroupedTabIds || []).filter((id) => keep.has(id));
      (state.tabGroups || []).forEach((g) => {
        g.tabIds = (g.tabIds || []).filter((id) => keep.has(id));
      });
    })
  ),
  resetGlobal: () => set({ sessions: {}, activeConversationId: null, openTabs: [], tabGroups: [], ungroupedTabIds: [] })
});

// src/store/sessionSlice.ts
import { produce as produce2 } from "immer";
var createEmptySession = (id) => ({
  id,
  messages: [],
  status: "IDLE",
  error: null,
  errorCode: null,
  retryAfterSec: null,
  activeToolCalls: /* @__PURE__ */ new Set(),
  isReasoning: false,
  tokenUsage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
  lastRunCost: void 0,
  contextUsage: void 0,
  runOverrides: {},
  versions: [],
  draftText: "",
  draftAttachments: [],
  outbox: void 0,
  scrollTop: 0,
  messageUI: {},
  lastAccessedAt: Date.now(),
  nextSuggestions: void 0
});
var createSessionSlice = (set, get) => ({
  sessions: {},
  initSession: (id) => set(
    produce2((state) => {
      if (!state.sessions[id]) state.sessions[id] = createEmptySession(id);
      state.sessions[id].lastAccessedAt = Date.now();
    })
  ),
  migrateSession: (oldId, newId) => set(
    produce2((state) => {
      const oldSession = state.sessions[oldId];
      if (oldSession) {
        state.sessions[newId] = { ...oldSession, id: newId };
        delete state.sessions[oldId];
        if (state.activeConversationId === oldId) {
          state.activeConversationId = newId;
        }
        const tabIndex = state.openTabs.findIndex((t) => t.id === oldId);
        if (tabIndex !== -1) {
          state.openTabs[tabIndex].id = newId;
        }
      }
    })
  ),
  pruneSessions: (maxCount = 20) => set(
    produce2((state) => {
      const ids = Object.keys(state.sessions);
      if (ids.length <= maxCount) return;
      const sorted = ids.sort((a, b) => state.sessions[a].lastAccessedAt - state.sessions[b].lastAccessedAt);
      const toRemove = sorted.slice(0, ids.length - maxCount);
      toRemove.forEach((id) => {
        if (id !== state.activeConversationId) delete state.sessions[id];
      });
    })
  ),
  setMessages: (cid, messages) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].messages = messages;
      state.sessions[cid].lastAccessedAt = Date.now();
    })
  ),
  addMessage: (cid, role, content = "", id) => {
    const msgId = id || createId();
    set(
      produce2((state) => {
        if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
        const s = state.sessions[cid];
        s.messages.push({
          id: msgId,
          role,
          createdAt: Date.now(),
          parts: content ? [{ type: "text", content }] : [],
          status: role === "assistant" ? "streaming" : "complete"
        });
        s.lastAccessedAt = Date.now();
        if (role === "user") {
          s.nextSuggestions = void 0;
        }
      })
    );
    return msgId;
  },
  updateMessage: (cid, msgId, updates) => set(
    produce2((state) => {
      const msg = state.sessions[cid]?.messages.find((m) => m.id === msgId);
      if (msg) Object.assign(msg, updates);
    })
  ),
  deleteMessage: (msgId) => set(
    produce2((state) => {
      const cid = state.activeConversationId;
      if (cid && state.sessions[cid]) {
        state.sessions[cid].messages = state.sessions[cid].messages.filter((m) => m.id !== msgId);
        delete state.sessions[cid].messageUI[msgId];
      }
    })
  ),
  /**
   * UPDATED: supports errorCode + retryAfterSec for quota UI.
   * - When status != ERROR, clears error fields.
   * - When status == ERROR, sets error and optional code/time.
   */
  setSessionStatus: (cid, status, error, errorCode, retryAfterSec) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      const s = state.sessions[cid];
      s.status = status;
      if (status === "ERROR") {
        s.error = error || "Unknown error";
        s.errorCode = errorCode ?? s.errorCode ?? null;
        s.retryAfterSec = retryAfterSec ?? s.retryAfterSec ?? null;
      } else {
        s.error = null;
        s.errorCode = null;
        s.retryAfterSec = null;
      }
    })
  ),
  appendDelta: (cid, msgId, delta) => set(
    produce2((state) => {
      const s = state.sessions[cid];
      if (!s) return;
      const msg = s.messages.find((m) => m.id === msgId);
      if (!msg) return;
      if (msg.role === "assistant") msg.status = "streaming";
      const lastPart = msg.parts[msg.parts.length - 1];
      if (lastPart && lastPart.type === "text") lastPart.content += delta;
      else msg.parts.push({ type: "text", content: delta });
    })
  ),
  appendThinkingDelta: (cid, msgId, delta) => set(
    produce2((state) => {
      const s = state.sessions[cid];
      if (!s) return;
      const msg = s.messages.find((m) => m.id === msgId);
      if (!msg) return;
      const lastPart = msg.parts[msg.parts.length - 1];
      if (lastPart && lastPart.type === "reasoning") lastPart.content += delta;
      else msg.parts.push({ type: "reasoning", content: delta, isComplete: false });
      s.isReasoning = true;
    })
  ),
  setMessageContent: (cid, msgId, content) => set(
    produce2((state) => {
      const s = state.sessions[cid];
      if (!s) return;
      const msg = s.messages.find((m) => m.id === msgId);
      if (!msg) return;
      msg.status = "complete";
      msg.parts.forEach((p) => {
        if (p.type === "reasoning") p.isComplete = true;
      });
      s.isReasoning = false;
      const textPart = msg.parts.find((p) => p.type === "text");
      if (textPart && textPart.type === "text") textPart.content = content;
      else msg.parts.push({ type: "text", content });
    })
  ),
  setDraft: (cid, text) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].draftText = text;
      state.sessions[cid].lastAccessedAt = Date.now();
    })
  ),
  addAttachment: (cid, att) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].draftAttachments.push(att);
    })
  ),
  removeAttachment: (cid, idOrFileId) => set(
    produce2((state) => {
      const s = state.sessions[cid];
      if (!s) return;
      s.draftAttachments = s.draftAttachments.filter((a) => a.clientId !== idOrFileId && a.fileId !== idOrFileId);
    })
  ),
  updateAttachment: (cid, clientId, updates) => set(
    produce2((state) => {
      const s = state.sessions[cid];
      if (!s) return;
      const idx = s.draftAttachments.findIndex((a) => a.clientId === clientId);
      if (idx === -1) return;
      s.draftAttachments[idx] = { ...s.draftAttachments[idx], ...updates };
    })
  ),
  clearDraft: (cid) => set(
    produce2((state) => {
      if (state.sessions[cid]) {
        state.sessions[cid].draftText = "";
        state.sessions[cid].draftAttachments = [];
      }
    })
  ),
  setOutbox: (cid, item) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].outbox = item;
    })
  ),
  clearOutbox: (cid) => set(
    produce2((state) => {
      if (state.sessions[cid]) state.sessions[cid].outbox = void 0;
    })
  ),
  markOutboxFailed: (cid) => set(
    produce2((state) => {
      const s = state.sessions[cid];
      if (s?.outbox) s.outbox.status = "failed";
    })
  ),
  addToolCall: (cid, msgId, tool) => set(
    produce2((state) => {
      const s = state.sessions[cid];
      if (!s) return;
      const msg = s.messages.find((m) => m.id === msgId);
      if (msg) msg.parts.push({ type: "tool_call", callId: tool.callId, name: tool.name, args: tool.args });
      s.activeToolCalls.add(tool.callId);
    })
  ),
  addToolResult: (cid, callId, result, name) => set(
    produce2((state) => {
      const s = state.sessions[cid];
      if (!s) return;
      s.activeToolCalls.delete(callId);
      s.messages.push({
        id: createId(),
        role: "tool",
        createdAt: Date.now(),
        parts: [{ type: "tool_result", callId, result, ...name ? { name } : {} }]
      });
    })
  ),
  setTokenUsage: (cid, usage) => set(
    produce2((state) => {
      if (state.sessions[cid]) state.sessions[cid].tokenUsage = usage;
    })
  ),
  setContextUsage: (cid, usage) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].contextUsage = usage;
    })
  ),
  setSessionRunOverrides: (cid, overrides) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].runOverrides = { ...state.sessions[cid].runOverrides, ...overrides };
    })
  ),
  clearSessionRunOverrides: (cid) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].runOverrides = {};
    })
  ),
  setScrollPosition: (cid, scrollTop) => set(
    produce2((state) => {
      const s = state.sessions[cid];
      if (s) {
        s.scrollTop = scrollTop;
        s.lastAccessedAt = Date.now();
      }
    })
  ),
  setMessageUI: (cid, messageId, updater) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      const s = state.sessions[cid];
      const prev = s.messageUI[messageId] || { renderers: {} };
      s.messageUI[messageId] = updater(prev);
    })
  ),
  setRendererUI: (cid, messageId, rendererKey, partial) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      const s = state.sessions[cid];
      const prev = s.messageUI[messageId] || { renderers: {} };
      const rendererPrev = prev.renderers[rendererKey] || {};
      s.messageUI[messageId] = {
        renderers: {
          ...prev.renderers,
          [rendererKey]: { ...rendererPrev, ...partial }
        }
      };
    })
  ),
  toggleRendererUI: (cid, messageId, rendererKey, flagKey) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      const s = state.sessions[cid];
      const prev = s.messageUI[messageId] || { renderers: {} };
      const rendererPrev = prev.renderers[rendererKey] || {};
      const current = !!rendererPrev[flagKey];
      s.messageUI[messageId] = {
        renderers: {
          ...prev.renderers,
          [rendererKey]: { ...rendererPrev, [flagKey]: !current }
        }
      };
    })
  ),
  setLastRunCost: (cid, cost) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].lastRunCost = cost;
    })
  ),
  setNextSuggestions: (cid, payload) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].nextSuggestions = {
        items: payload.items || [],
        forAssistantMessageId: payload.forAssistantMessageId || null,
        forUserMessageId: payload.forUserMessageId || null,
        createdAt: payload.createdAt || Date.now()
      };
    })
  ),
  setScrollAnchor: (cid, anchor) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].scrollAnchor = anchor;
    })
  ),
  setLastSyncedAt: (cid, iso) => set(
    produce2((state) => {
      if (!state.sessions[cid]) state.sessions[cid] = createEmptySession(cid);
      state.sessions[cid].lastSyncedAt = iso || null;
    })
  )
});

// src/utils/storage.ts
var noopStorage = {
  length: 0,
  clear: () => {
  },
  getItem: () => null,
  key: () => null,
  removeItem: () => {
  },
  setItem: () => {
  }
};
function validateStorage(storage) {
  if (!storage) return null;
  try {
    const key = "__dopomogai_storage_test__";
    storage.setItem(key, key);
    storage.removeItem(key);
    return storage;
  } catch {
    return null;
  }
}
function getSafeLocalStorage() {
  if (typeof localStorage === "undefined") return null;
  return validateStorage(localStorage);
}
function getSafeSessionStorage() {
  if (typeof sessionStorage === "undefined") return null;
  return validateStorage(sessionStorage);
}
function getPersistStorage() {
  return getSafeLocalStorage() || noopStorage;
}

// src/store/persistKey.ts
var DEFAULT_CHAT_PERSIST_KEY = "dopomogai-chat-registry";
var encodePart = (value) => encodeURIComponent(value.trim());
var buildChatPersistNamespace = ({
  namespace,
  authMode,
  agentId,
  interactionMode,
  widgetVersionId,
  installationId,
  templateId
} = {}) => {
  if (namespace && namespace.trim()) {
    return encodePart(namespace);
  }
  const scope = installationId ? `installation:${installationId}` : widgetVersionId ? `widget:${widgetVersionId}` : templateId ? `template:${templateId}` : agentId ? `agent:${agentId}` : "global";
  const parts = ["v2", authMode || "WIDGET", scope];
  if (interactionMode) {
    parts.push(`interaction:${interactionMode}`);
  }
  return parts.map(encodePart).join(":");
};
var buildChatPersistKey = (namespace) => namespace ? `${DEFAULT_CHAT_PERSIST_KEY}:${namespace}` : DEFAULT_CHAT_PERSIST_KEY;

// src/store/index.ts
var MAX_PERSISTED_MESSAGES = 200;
var partializeSession = (session) => {
  const { activeToolCalls: _activeToolCalls, ...rest } = session;
  return {
    ...rest,
    messages: Array.isArray(session.messages) ? session.messages.slice(-MAX_PERSISTED_MESSAGES) : [],
    outbox: session.outbox ? { ...session.outbox } : void 0,
    isReasoning: false,
    status: "IDLE",
    error: null,
    errorCode: null,
    retryAfterSec: null,
    scrollAnchor: session.scrollAnchor ?? null,
    lastSyncedAt: session.lastSyncedAt ?? null
  };
};
var createChatStore = (options = {}) => {
  const persistKey = options.persistKey || DEFAULT_CHAT_PERSIST_KEY;
  const persistNamespace = options.persistNamespace;
  return createStore()(
    persist(
      (...args) => ({
        ...createGlobalSlice(...args),
        ...createSessionSlice(...args)
      }),
      {
        name: persistKey,
        storage: createJSONStorage(getPersistStorage),
        partialize: (state) => ({
          activeConversationId: state.activeConversationId,
          isSidebarCollapsed: state.isSidebarCollapsed,
          openTabs: state.openTabs,
          tabGroups: state.tabGroups,
          ungroupedTabIds: state.ungroupedTabIds,
          config: state.config,
          sessions: Object.fromEntries(
            Object.entries(state.sessions).map(([id, s]) => [id, partializeSession(s)])
          ),
          conversations: state.conversations,
          runOverrides: state.runOverrides
        }),
        onRehydrateStorage: () => (state) => {
          if (!state) return;
          state.runOverrides = state.runOverrides || {};
          state.openTabs = state.openTabs || [];
          state.tabGroups = state.tabGroups || [];
          state.ungroupedTabIds = state.ungroupedTabIds || [];
          state.config = state.config || { agentId: null, apiBaseUrl: "", compatApiFallbackEnabled: true };
          state.config.compatApiFallbackEnabled = state.config.compatApiFallbackEnabled !== false;
          state.config.features = {
            threadsEnabled: false,
            commentsEnabled: false,
            attachmentsEnabled: true,
            historyEnabled: true,
            tabsEnabled: true,
            ...state.config.features || {}
          };
          state.config.authMode = state.config.authMode || "WIDGET";
          state.config.interactionMode = state.config.interactionMode || null;
          state.config.workspaceMode = state.config.workspaceMode || null;
          state.config.flowStep = state.config.flowStep ?? null;
          state.config.readinessSummary = state.config.readinessSummary ?? null;
          state.config.persistNamespace = state.config.persistNamespace || persistNamespace;
          if (state.activeConversationId && !state.sessions[state.activeConversationId]) {
            state.activeConversationId = null;
          }
          Object.values(state.sessions).forEach((s) => {
            s.activeToolCalls = /* @__PURE__ */ new Set();
            s.isReasoning = false;
            s.status = "IDLE";
            s.error = null;
            s.errorCode = null;
            s.retryAfterSec = null;
            s.runOverrides = s.runOverrides || {};
            s.scrollAnchor = s.scrollAnchor || null;
            s.lastSyncedAt = s.lastSyncedAt || null;
            s.messageUI = s.messageUI || {};
            s.lastAccessedAt = s.lastAccessedAt || Date.now();
          });
        }
      }
    )
  );
};

// src/core/ConversationManager.ts
import { conversationService, filesService, isApiError } from "@dopomogai/api-client";

// src/utils/files.ts
var FILE_ID_RE = /\/api\/v1\/files\/([0-9a-fA-F-]{36})\/download/;
function parseFileIdFromUrl(url) {
  if (!url) return void 0;
  const match = url.match(FILE_ID_RE);
  return match ? match[1] : void 0;
}
function filenameFromUrl(url) {
  if (!url) return void 0;
  try {
    const parsed = new URL(url, "http://placeholder");
    const path = parsed.pathname || "";
    const parts = path.split("/").filter(Boolean);
    const last = parts[parts.length - 1] || "";
    if (last.toLowerCase() === "download" && parts.length >= 2) {
      return decodeURIComponent(parts[parts.length - 2]);
    }
    return decodeURIComponent(last);
  } catch {
    const parts = url.split("/").filter(Boolean);
    return decodeURIComponent(parts[parts.length - 1] || "Attachment");
  }
}

// src/core/StateMapper.ts
var StateMapper = class {
  /**
   * Transforms a full Backend Conversation Detail (ModelMessages dump) into UI-ready Messages.
   */
  static mapConversationToState(backendMessages) {
    if (!Array.isArray(backendMessages)) return [];
    return backendMessages.map((msg) => this.mapSingleMessage(msg));
  }
  /**
   * Maps a single backend message to the frontend format.
   *
   * Goal (per validated plan):
   * - Attach tool name to tool_result parts when possible.
   * - Best effort within the same backend message: build callId -> toolName map from tool-call parts.
   *
   * NOTE:
   * - If a tool_result appears in a different message than the tool_call, name may be missing.
   * - Frontend can still resolve via callId scanning in conversation history.
   */
  static mapSingleMessage(msg) {
    const parts = [];
    const messageId = msg.id || createId();
    const rawParts = Array.isArray(msg.parts) ? msg.parts : [];
    const callIdToName = /* @__PURE__ */ new Map();
    for (const p of rawParts) {
      if (p?.part_kind === "tool-call") {
        const callId = p.tool_call_id || "";
        const toolName = p.tool_name || "unknown";
        if (callId) callIdToName.set(callId, toolName);
      }
    }
    rawParts.forEach((p) => {
      if (p.part_kind === "text" || p.part_kind === "user-prompt" || p.part_kind === "llm-response") {
        this.processContent(p.content, parts, messageId);
      } else if (p.part_kind === "tool-call") {
        const callId = p.tool_call_id || createId();
        const toolName = p.tool_name || "unknown";
        parts.push({
          type: "tool_call",
          callId,
          name: toolName,
          args: p.args || {}
        });
      } else if (p.part_kind === "tool-result" || p.part_kind === "tool-return") {
        const callId = p.tool_call_id || "";
        const name = p.tool_name || (callId ? callIdToName.get(callId) : void 0);
        parts.push({
          type: "tool_result",
          callId,
          // Backward compatible optional field (types updated in packages/types)
          ...name ? { name } : {},
          result: p.content || p.output
        });
      }
    });
    return {
      id: messageId,
      role: this.mapRole(msg.sender || msg.kind),
      createdAt: msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now(),
      status: "complete",
      parts
    };
  }
  /**
   * Processes the `content` field, which can be a String OR an Array of mixed types.
   * Fixes the "File: <name>" redundancy and resolves filenames/fileIds.
   */
  static processContent(content, targetParts, messageId) {
    if (typeof content === "string") {
      targetParts.push({ type: "text", content });
      return;
    }
    if (Array.isArray(content)) {
      for (let i = 0; i < content.length; i++) {
        const item = content[i];
        if (typeof item === "string") {
          const nextItem = content[i + 1];
          const isLabel = this.isRedundantFileLabel(item, nextItem);
          if (!isLabel) {
            targetParts.push({ type: "text", content: item });
          }
        } else if (item && typeof item === "object") {
          const isFile = item.kind === "document-url" || item.kind === "image-url" || !!item.url;
          if (!isFile) continue;
          let name;
          const prev = content[i - 1];
          if (typeof prev === "string") {
            const trimmed = prev.trim();
            if (trimmed.toLowerCase().startsWith("file: ")) name = trimmed.slice(6);
            else if (trimmed.toLowerCase().startsWith("image: ")) name = trimmed.slice(7);
          }
          if (!name && item.filename) name = item.filename;
          if (!name) name = filenameFromUrl(item.url) || "Attachment";
          const fileId = parseFileIdFromUrl(item.url);
          const mediaType = item.media_type || item.mime_type || "application/octet-stream";
          targetParts.push({
            type: "ui",
            id: item.identifier || createId(),
            component: "file-bubble",
            props: {
              url: item.url,
              name,
              type: String(mediaType).startsWith("image/") ? "image" : "file",
              mimeType: mediaType,
              fileId,
              messageId
              // enable per-message actions like "remove from message"
            }
          });
        }
      }
    }
  }
  static isRedundantFileLabel(currentStr, nextItem) {
    if (!nextItem || typeof nextItem !== "object") return false;
    const trimmed = currentStr.trim().toLowerCase();
    return trimmed.startsWith("file: ") || trimmed.startsWith("image: ");
  }
  static mapRole(sender) {
    if (sender === "user" || sender === "request") return "user";
    if (sender === "agent" || sender === "assistant" || sender === "response" || sender === "model") return "assistant";
    return "system";
  }
};

// src/core/ConversationManager.ts
var STALE_THRESHOLD_MS = 1e3 * 60 * 5;
var MAX_MEMORY_SESSIONS = 21;
var ConversationManager = class {
  constructor(store, config, _authProvider) {
    this.store = store;
    this.config = config;
    this._authProvider = _authProvider;
    this.clientRef = null;
  }
  setClient(client) {
    this.clientRef = client;
  }
  async listConversations() {
    try {
      const response = await conversationService.listConversations(this.config.agentId, 50);
      if (isApiError(response)) throw new Error(response.message);
      const items = response.items || [];
      const summaries = items.map((c) => ({
        id: c.id,
        title: c.title || "New Chat",
        first_user_message_preview: c.first_user_message_preview || "New Conversation",
        created_at: c.created_at,
        updated_at: c.updated_at,
        agent_id: c.agent_id,
        user_id: c.user_id,
        message_count: c.message_count ?? 0
      }));
      this.store.getState().setConversations(summaries);
    } catch (error) {
      console.error("Failed to list conversations:", error);
    }
  }
  async loadConversation(conversationId) {
    const state = this.store.getState();
    state.setActiveConversationId(conversationId);
    const session = state.sessions[conversationId];
    if (!session) {
      state.setSessionStatus(conversationId, "CONNECTING");
      await this.fetchAndHydrate(conversationId);
      state.pruneSessions(MAX_MEMORY_SESSIONS);
      return;
    }
    if (session.messages.length === 0 && session.status === "IDLE") {
      const isBrandNewLocal = Date.now() - session.lastAccessedAt < 1e3;
      if (!isBrandNewLocal) {
        await this.fetchAndHydrate(conversationId);
      }
      return;
    }
    const isStale = Date.now() - session.lastAccessedAt > STALE_THRESHOLD_MS;
    if (isStale && session.status !== "CONNECTING" && session.status !== "CONNECTED") {
      console.log(`[ConversationManager] Session ${conversationId} is stale. Refreshing in background...`);
      void this.fetchAndHydrate(conversationId, true);
    }
  }
  /**
   * Internal worker to fetch and map data.
   * @param isBackground - If true, suppress status spinners and error toasts.
   */
  async fetchAndHydrate(conversationId, isBackground = false) {
    try {
      const response = await conversationService.getConversationById(conversationId, true);
      if (isApiError(response)) {
        if (response.status === 404) return;
        throw new Error(response.message);
      }
      const usageRaw = response.usage || response.token_usage || response.tokenUsage || response.usage_summary || null;
      if (usageRaw) {
        const prompt = usageRaw.input_tokens ?? usageRaw.promptTokens ?? usageRaw.prompt_tokens ?? 0;
        const completion = usageRaw.output_tokens ?? usageRaw.completionTokens ?? usageRaw.completion_tokens ?? 0;
        const total = usageRaw.total_tokens ?? usageRaw.totalTokens ?? Number(prompt || 0) + Number(completion || 0);
        this.store.getState().setTokenUsage(conversationId, {
          promptTokens: Number(prompt) || 0,
          completionTokens: Number(completion) || 0,
          totalTokens: Number(total) || 0
        });
      }
      const messages = StateMapper.mapConversationToState(response.messages);
      this.store.getState().setMessages(conversationId, messages);
      this.store.getState().setLastSyncedAt(conversationId, (/* @__PURE__ */ new Date()).toISOString());
      if (!isBackground) {
        this.store.getState().setSessionStatus(conversationId, "IDLE");
      }
      this.loadVersions(conversationId);
    } catch (error) {
      if (!isBackground) {
        console.error(`Failed to load conversation ${conversationId}:`, error);
        this.store.getState().setSessionStatus(conversationId, "ERROR", `Could not load chat: ${error.message || error}`);
      }
    }
  }
  async loadVersions(_conversationId) {
    try {
    } catch {
    }
  }
  async deleteConversation(conversationId) {
    const state = this.store.getState();
    const next = state.conversations.filter((c) => c.id !== conversationId);
    state.setConversations(next);
    if (state.activeConversationId === conversationId) {
      state.setActiveConversationId(null);
    }
    try {
      const res = await conversationService.deleteConversation(conversationId);
      if (isApiError(res)) throw new Error(res.message);
    } catch (e) {
      console.error("Delete failed:", e);
    }
  }
  async renameConversation(conversationId, newTitle) {
    this.store.getState().updateConversationMeta(conversationId, { title: newTitle });
    try {
      const res = await conversationService.updateConversationMeta(conversationId, newTitle);
      if (isApiError(res)) throw new Error(res.message);
    } catch (e) {
      console.error("Rename failed:", e);
    }
  }
  async revertToVersion(versionId) {
    const state = this.store.getState();
    const conversationId = state.activeConversationId;
    if (!conversationId) return;
    try {
      state.setSessionStatus(conversationId, "CONNECTING");
      const res = await conversationService.revertConversation(conversationId, versionId);
      if (isApiError(res)) throw new Error(res.message);
      await this.fetchAndHydrate(conversationId);
    } catch (e) {
      state.setSessionStatus(conversationId, "ERROR", `Revert failed: ${e.message || e}`);
    }
  }
  async forkFromVersion(versionId) {
    const state = this.store.getState();
    const currentId = state.activeConversationId;
    if (!currentId) return;
    try {
      state.setSessionStatus(currentId, "CONNECTING");
      const res = await conversationService.branchFromVersion(currentId, versionId);
      if (isApiError(res)) throw new Error(res.message);
      const newId = res.new_conversation_id;
      void this.loadConversation(newId);
      void this.listConversations();
    } catch (e) {
      state.setSessionStatus(currentId, "ERROR", `Fork failed: ${e.message || e}`);
    }
  }
  async branchAndEdit(originalConversationId, messageId, newContent) {
    const state = this.store.getState();
    if (!this.clientRef) return;
    const session = state.sessions[originalConversationId];
    if (!session) return;
    const msgIndex = session.messages.findIndex((m) => m.id === messageId);
    if (msgIndex === -1) return;
    const branchIndex = Math.max(0, msgIndex - 1);
    try {
      state.setSessionStatus(originalConversationId, "CONNECTING");
      const branchRes = await conversationService.branchConversation(originalConversationId, branchIndex);
      if (isApiError(branchRes)) throw new Error(branchRes.message);
      const newId = branchRes.new_conversation_id;
      state.initSession(newId);
      state.setActiveConversationId(newId);
      const slicedMessages = session.messages.slice(0, branchIndex + 1);
      state.setMessages(newId, slicedMessages);
      await this.clientRef.sendMessage(newId, newContent);
      void this.listConversations();
    } catch (error) {
      console.error("Branching failed:", error);
      state.setSessionStatus(originalConversationId, "ERROR", "Failed to edit message.");
    }
  }
  /**
   * Edit a message in-place within the same conversation.
   * - Optionally update text content
   * - Optionally add/remove file attachments by file_id
   * - Does not auto-rerun; caller may invoke rerunFromIndex separately
   */
  async editMessageInPlace(conversationId, messageId, params) {
    try {
      const res = await conversationService.getConversationById(conversationId, true);
      if (isApiError(res)) throw new Error(res.message || "Failed to fetch conversation");
      const payload = res;
      const messages = Array.isArray(payload.messages) ? payload.messages : [];
      const msgIndex = messages.findIndex((m) => m.id === messageId);
      if (msgIndex < 0) throw new Error("Message not found");
      const target = messages[msgIndex];
      const newMsg = {
        ...target,
        parts: Array.isArray(target.parts) ? target.parts.map((p) => ({ ...p })) : []
      };
      if (typeof params.newText === "string") {
        for (const part of newMsg.parts) {
          if (part.part_kind === "user-prompt" || part.part_kind === "llm-response") {
            part.content = params.newText;
            break;
          }
        }
        if (!newMsg.parts.some((p) => p.part_kind === "user-prompt" || p.part_kind === "llm-response")) {
          newMsg.parts.push({ part_kind: "llm-response", content: params.newText });
        }
      }
      let arrayContentIdx = newMsg.parts.findIndex((p) => Array.isArray(p.content));
      if (arrayContentIdx < 0) {
        newMsg.parts.push({ part_kind: "llm-response", content: [] });
        arrayContentIdx = newMsg.parts.length - 1;
      }
      if (params.removeFileIds && params.removeFileIds.length) {
        const ids = new Set(params.removeFileIds);
        for (const part of newMsg.parts) {
          if (!Array.isArray(part.content)) continue;
          const nextContent = [];
          for (let i = 0; i < part.content.length; i++) {
            const item = part.content[i];
            if (item && typeof item === "object") {
              const isFile = item.kind === "document-url" || item.kind === "image-url" || !!item.url;
              if (isFile) {
                const fid = parseFileIdFromUrl(item.url);
                if (fid && ids.has(fid)) {
                  const prev = part.content[i - 1];
                  const prevIsLabel = typeof prev === "string" && (prev.trim().toLowerCase().startsWith("file: ") || prev.trim().toLowerCase().startsWith("image: "));
                  if (prevIsLabel && nextContent.length > 0 && typeof nextContent[nextContent.length - 1] === "string") {
                    nextContent.pop();
                  }
                  continue;
                }
              }
            }
            nextContent.push(item);
          }
          part.content = nextContent;
        }
      }
      if (params.addFiles && params.addFiles.length) {
        const holder = newMsg.parts[arrayContentIdx];
        const contentArr = Array.isArray(holder.content) ? holder.content : [];
        for (const f of params.addFiles) {
          const url = filesService.getDownloadUrl(f.file_id);
          const kind = (f.media_type || "").startsWith("image/") ? "image-url" : "document-url";
          contentArr.push({ kind, url, media_type: f.media_type || "application/octet-stream" });
        }
        holder.content = contentArr;
      }
      const operations = [{ op: "replace", index: msgIndex, message: newMsg }];
      const patchRes = await conversationService.patchConversationMessages(conversationId, operations);
      if (isApiError(patchRes)) throw new Error(patchRes.message || "Patch failed");
      await this.loadConversation(conversationId);
      return true;
    } catch (e) {
      console.error("[ConversationManager] editMessageInPlace failed", e);
      return false;
    }
  }
  /**
   * Rerun from a specific message boundary.
   * - Deletes all messages after the boundary message
   * - Caller can optionally send a new message or let the next turn regenerate
   */
  async rerunFromIndex(conversationId, boundaryMessageId) {
    try {
      const res = await conversationService.getConversationById(conversationId, true);
      if (isApiError(res)) throw new Error(res.message || "Failed to fetch conversation");
      const payload = res;
      const messages = Array.isArray(payload.messages) ? payload.messages : [];
      const idx = messages.findIndex((m) => m.id === boundaryMessageId);
      if (idx < 0) throw new Error("Message not found");
      const operations = [];
      for (let i = messages.length - 1; i > idx; i--) {
        operations.push({ op: "delete", index: i });
      }
      if (operations.length === 0) return true;
      const patchRes = await conversationService.patchConversationMessages(conversationId, operations);
      if (isApiError(patchRes)) throw new Error(patchRes.message || "Patch failed");
      await this.loadConversation(conversationId);
      return true;
    } catch (e) {
      console.error("[ConversationManager] rerunFromIndex failed", e);
      return false;
    }
  }
};

// src/network/sse.ts
var HttpError = class extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
    this.name = "HttpError";
  }
};
var StreamConnection = class {
  constructor(url, headers, onEvent, onError, onTokenRefresh, onConversationId) {
    this.abortController = null;
    this.url = url;
    this.headers = headers;
    this.onEvent = onEvent;
    this.onError = onError;
    this.onTokenRefresh = onTokenRefresh;
    this.onConversationId = onConversationId;
  }
  async connect(body) {
    this.abortController = new AbortController();
    try {
      const response = await fetch(this.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...this.headers
        },
        body: JSON.stringify(body),
        signal: this.abortController.signal
      });
      const newWat = response.headers.get("X-New-WAT") || response.headers.get("x-new-wat");
      if (newWat && this.onTokenRefresh) this.onTokenRefresh(newWat);
      const convId = response.headers.get("X-Conversation-Id") || response.headers.get("x-conversation-id");
      if (convId && this.onConversationId) this.onConversationId(convId);
      if (!response.ok) {
        let text = "";
        try {
          text = await response.text();
        } catch {
        }
        throw new HttpError(response.status, `Stream failed: ${response.status} ${text}`.trim());
      }
      if (!response.body) throw new HttpError(0, "No response body");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split(/\r\n\r\n|\r\r|\n\n/);
        buffer = parts.pop() || "";
        for (const block of parts) {
          if (!block.trim()) continue;
          const lines = block.split(/\r\n|\r|\n/);
          let dataBuffer = "";
          for (const line of lines) {
            if (line.startsWith("data: ")) dataBuffer += line.substring(6);
            else if (line.startsWith("data:")) dataBuffer += line.substring(5);
          }
          if (dataBuffer) {
            if (dataBuffer.trim() === "[DONE]") return;
            try {
              const json = JSON.parse(dataBuffer);
              this.onEvent(json);
            } catch (e) {
              if (!dataBuffer.startsWith(":")) console.warn("Dropping malformed SSE JSON:", dataBuffer);
            }
          }
        }
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      this.onError(err);
    }
  }
  abort() {
    this.abortController?.abort();
  }
};

// src/core/StreamHandler.ts
import { conversationService as conversationService2, isApiError as isApiError2 } from "@dopomogai/api-client";
function readQuotaInfo(evt) {
  const code = evt?.error_code || evt?.errorCode || evt?.code || void 0;
  const retryAfterSec = typeof evt?.retry_after_sec === "number" ? evt.retry_after_sec : typeof evt?.retryAfterSec === "number" ? evt.retryAfterSec : void 0;
  return { code, retryAfterSec };
}
var StreamHandler = class {
  constructor(store, config, authProvider) {
    this.store = store;
    this.config = config;
    this.authProvider = authProvider;
    this.connections = /* @__PURE__ */ new Map();
  }
  async connect(conversationId, payload, assistantMessageId, endpointOverride, attempt = 0) {
    this.disconnect(conversationId);
    const headers = await this.authProvider.getHeaders();
    const url = endpointOverride || `${this.config.apiBaseUrl}/api/v1/agents/${this.config.agentId}/conversations/stream`;
    const connection = new StreamConnection(
      url,
      headers,
      (event) => this.routeEvent(conversationId, assistantMessageId, event),
      async (err) => {
        if (err && typeof err.status === "number" && err.status === 401 && attempt === 0 && this.config.refreshAuth) {
          try {
            const newToken = await this.config.refreshAuth();
            this.authProvider.onTokenRefresh?.(newToken);
            await this.connect(conversationId, payload, assistantMessageId, endpointOverride, 1);
            return;
          } catch {
          }
        }
        this.handleError(conversationId, err);
      },
      (newToken) => this.authProvider.onTokenRefresh?.(newToken),
      (serverConvId) => {
        if (serverConvId && serverConvId !== conversationId) {
          console.warn(`[StreamHandler] ID Mismatch: Client=${conversationId}, Server=${serverConvId}.`);
        }
      }
    );
    this.connections.set(conversationId, connection);
    this.store.getState().setSessionStatus(conversationId, "CONNECTING");
    try {
      const finalPayload = { ...payload, conversationId };
      await connection.connect(finalPayload);
    } catch (error) {
      this.handleError(conversationId, error);
    }
  }
  disconnect(conversationId) {
    const conn = this.connections.get(conversationId);
    if (conn) {
      conn.abort();
      this.connections.delete(conversationId);
      const session = this.store.getState().sessions[conversationId];
      if (session && (session.status === "CONNECTING" || session.status === "CONNECTED")) {
        this.store.getState().setSessionStatus(conversationId, "IDLE");
      }
    }
  }
  disconnectAll() {
    for (const id of this.connections.keys()) {
      this.disconnect(id);
    }
  }
  routeEvent(conversationId, currentMsgId, event) {
    const store = this.store.getState();
    const evt = event;
    if (!store.sessions[conversationId]) return;
    const markConnected = () => {
      if (store.sessions[conversationId]?.status !== "CONNECTED") {
        store.setSessionStatus(conversationId, "CONNECTED");
      }
    };
    switch (evt.type) {
      case "text_delta":
      case "response_chunk": {
        const text = evt.delta || evt.content || "";
        if (text) {
          store.appendDelta(conversationId, evt.messageId || currentMsgId, text);
          markConnected();
        }
        break;
      }
      case "thinking_delta": {
        const thought = evt.delta || "";
        if (thought) {
          store.appendThinkingDelta(conversationId, evt.messageId || currentMsgId, thought);
          markConnected();
        }
        break;
      }
      case "tool_call": {
        store.addToolCall(conversationId, currentMsgId, { callId: evt.callId, name: evt.name, args: evt.args || {} });
        markConnected();
        break;
      }
      case "tool_result": {
        const callId = evt.callId || createId();
        const name = evt.name;
        store.addToolResult(conversationId, callId, evt.result, name);
        markConnected();
        break;
      }
      case "progress_update": {
        const toolName = evt.tool || evt.content?.tool_name || "unknown_tool";
        const callId = evt.tool_call_id || evt.content?.tool_call_id || createId();
        const args = evt.args || evt.content?.args || {};
        store.addToolCall(conversationId, currentMsgId, { callId, name: toolName, args });
        markConnected();
        break;
      }
      case "ui_render": {
        const mid = evt.messageId || currentMsgId;
        const s = this.store.getState();
        const session = s.sessions[conversationId];
        const msg = session?.messages.find((m) => m.id === mid);
        if (msg) {
          msg.parts.push({ type: "ui", id: evt.id || createId(), component: evt.component, props: evt.props || {} });
          s.setSessionStatus(conversationId, "CONNECTED");
        }
        break;
      }
      case "complete": {
        if (evt.final_response) {
          store.setMessageContent(conversationId, evt.messageId || currentMsgId, evt.final_response);
          markConnected();
        }
        break;
      }
      case "status_change": {
        const s = evt.status;
        if (s === "thinking" || s === "executing" || s === "streaming") {
          store.setSessionStatus(conversationId, "CONNECTED");
        }
        break;
      }
      case "error": {
        const msg = evt.message || "Unknown error";
        const { code, retryAfterSec } = readQuotaInfo(evt);
        store.setSessionStatus(conversationId, "ERROR", msg, code, retryAfterSec);
        this.store.getState().markOutboxFailed?.(conversationId);
        this.disconnect(conversationId);
        break;
      }
      case "stream_end": {
        if (evt.usage) {
          const prompt = Number(evt.usage.promptTokens || evt.usage.prompt_tokens || 0);
          const completion = Number(evt.usage.completionTokens || evt.usage.completion_tokens || 0);
          const total = Number(evt.usage.totalTokens || evt.usage.total_tokens || prompt + completion);
          store.setTokenUsage(conversationId, { promptTokens: prompt, completionTokens: completion, totalTokens: total });
          const credits = evt.usage.credits_charged ?? evt.usage.creditsCharged;
          const prov = evt.usage.provider_cost_usd ?? evt.usage.providerCostUsd;
          const user = evt.usage.total_cost_usd ?? evt.usage.totalCostUsd;
          if (credits != null || prov != null || user != null) {
            store.setLastRunCost?.(conversationId, {
              creditsCharged: credits != null ? Number(credits) : null,
              providerCostUsd: prov != null ? Number(prov) : null,
              totalCostUsd: user != null ? Number(user) : null
            });
          }
        }
        this.store.getState().clearOutbox?.(conversationId);
        void this.refreshConversationList();
        this.disconnect(conversationId);
        break;
      }
    }
  }
  async refreshConversationList() {
    try {
      const resp = await conversationService2.listConversations(this.config.agentId, 50);
      if (isApiError2(resp)) return;
      const items = resp.items || [];
      const summaries = items.map((c) => ({
        id: c.id,
        title: c.title || "New Chat",
        first_user_message_preview: c.first_user_message_preview || "New Conversation",
        created_at: c.created_at,
        updated_at: c.updated_at,
        agent_id: c.agent_id,
        user_id: c.user_id,
        message_count: c.message_count ?? 0
      }));
      this.store.getState().setConversations(summaries);
    } catch {
    }
  }
  handleError(conversationId, error) {
    if (error?.name === "AbortError") return;
    console.error(`[StreamHandler] Network Error in ${conversationId}:`, error);
    const msg = error?.message || "Connection failed";
    this.store.getState().setSessionStatus(conversationId, "ERROR", msg);
    this.store.getState().markOutboxFailed?.(conversationId);
    this.connections.delete(conversationId);
  }
};

// src/client/ChatClient.ts
import { filesService as filesService3, conversationService as conversationService3, fileGroupsService, fsService, filesLibraryService } from "@dopomogai/api-client";

// src/client/attachments.ts
import { filesService as filesService2 } from "@dopomogai/api-client";
function getAttachmentFileId(att) {
  if (att.source?.kind === "file") return att.source.fileId;
  return att.fileId || parseFileIdFromUrl(att.url);
}
function isKbPathAttachment(att) {
  if (att.source?.kind === "kb_path") return !!att.source.projectId && !!att.source.path;
  const legacyProjectId = att.kbProjectId;
  const legacyPath = att.kbPath;
  return !!legacyProjectId && !!legacyPath;
}
function getUploadedDraftAttachments(draftAttachments) {
  return (draftAttachments || []).filter((a) => a.status === "uploaded" && (getAttachmentFileId(a) || isKbPathAttachment(a)));
}
function uploadedAttachmentsToFileIds(attachments) {
  return (attachments || []).map((a) => getAttachmentFileId(a)).filter(Boolean);
}
function addFileBubblesToUserMessageParts(opts) {
  const { existingParts, userMsgId, uploadedAttachments } = opts;
  const out = Array.isArray(existingParts) ? existingParts.slice() : [];
  uploadedAttachments.forEach((att) => {
    const fileId = getAttachmentFileId(att);
    const url = fileId ? filesService2.getDownloadUrl(fileId) : att.url || "";
    out.push({
      type: "ui",
      id: fileId || att.clientId,
      component: "file-bubble",
      props: {
        url,
        name: att.name,
        type: att.type,
        messageId: userMsgId,
        fileId,
        status: "uploaded"
      }
    });
  });
  return out;
}
function attachmentsToRequestParts(uploadedAttachments) {
  const parts = [];
  uploadedAttachments.forEach((att) => {
    const anyAtt = att;
    const source = att.source;
    if (source?.kind === "kb_path") {
      parts.push({
        type: "file",
        kb_project_id: source.projectId,
        path: source.path,
        filename: att.name,
        media_type: "application/octet-stream"
      });
      return;
    }
    if (anyAtt.kbProjectId && anyAtt.kbPath) {
      parts.push({
        type: "file",
        kb_project_id: anyAtt.kbProjectId,
        path: anyAtt.kbPath,
        filename: att.name,
        media_type: "application/octet-stream"
      });
      return;
    }
    const fid = getAttachmentFileId(att);
    if (fid) parts.push({ type: "file", file_id: fid, media_type: "application/octet-stream" });
  });
  return parts;
}

// src/client/tools.ts
async function submitToolResultWithGate(opts) {
  const { store, allowClientTools, sendMessage, callId, result, name } = opts;
  if (!allowClientTools) {
    console.warn("[ChatClient] submitToolResult ignored: client-side tools disabled.");
    return;
  }
  const state = store.getState();
  const activeId = state.activeConversationId;
  let targetId = activeId;
  if (targetId && !state.sessions[targetId]?.activeToolCalls.has(callId)) {
    targetId = Object.keys(state.sessions).find((sid) => state.sessions[sid].activeToolCalls.has(callId)) || null;
  }
  if (!targetId) {
    console.warn(`Tool result submitted for unknown callId ${callId}`);
    return;
  }
  const toolResults = [{ callId, name, result }];
  state.addToolResult(targetId, callId, result, name);
  await sendMessage(targetId, "", { tool_results: toolResults });
}

// src/client/ChatClient.ts
var ChatClient = class {
  constructor(config) {
    this.config = config;
    this.store = createChatStore({ persistNamespace: config.persistNamespace, persistKey: buildChatPersistKey(config.persistNamespace) });
    this.streamHandler = new StreamHandler(
      this.store,
      {
        apiBaseUrl: config.apiBaseUrl,
        agentId: config.agentId,
        refreshAuth: config.refreshAuth
      },
      config.authProvider
    );
    this.conversationManager = new ConversationManager(
      this.store,
      { apiBaseUrl: config.apiBaseUrl, agentId: config.agentId },
      config.authProvider
    );
    this.conversationManager.setClient(this);
    this.store.getState().config = {
      agentId: config.agentId,
      apiBaseUrl: config.apiBaseUrl,
      authMode: config.authMode || "WIDGET",
      preset: config.preset,
      installationId: config.installationId,
      widgetVersionId: config.widgetVersionId,
      templateId: config.templateId,
      compatApiFallbackEnabled: config.compatApiFallbackEnabled !== false,
      interactionMode: config.interactionMode || null,
      workspaceMode: config.workspaceMode || null,
      flowStep: config.flowStep ?? null,
      readinessSummary: config.readinessSummary ?? null,
      persistNamespace: config.persistNamespace,
      assistantProfileId: config.assistantProfileId ?? null,
      runtimeAgentId: config.runtimeAgentId ?? null,
      subjectAgentId: config.subjectAgentId ?? null,
      subjectContextKey: config.subjectContextKey ?? null,
      onboardingSessionId: config.onboardingSessionId ?? null,
      scanId: config.scanId ?? null,
      features: {
        threadsEnabled: false,
        commentsEnabled: false,
        attachmentsEnabled: true,
        historyEnabled: true,
        tabsEnabled: true,
        ...config.features || {}
      }
    };
  }
  buildRuntimeSubjectContext(overrides = {}) {
    return {
      assistant_profile_id: overrides.assistant_profile_id ?? this.config.assistantProfileId ?? null,
      runtime_agent_id: overrides.runtime_agent_id ?? this.config.runtimeAgentId ?? null,
      subject_agent_id: overrides.subject_agent_id ?? this.config.subjectAgentId ?? null,
      subject_context_key: overrides.subject_context_key ?? this.config.subjectContextKey ?? null,
      onboarding_session_id: overrides.onboarding_session_id ?? this.config.onboardingSessionId ?? null,
      scan_id: overrides.scan_id ?? this.config.scanId ?? null
    };
  }
  buildAssistantContextEnvelope(overrides = {}) {
    const runtimeSubjectContext = this.buildRuntimeSubjectContext(overrides);
    return {
      agent_id: overrides.agent_id ?? this.config.agentId,
      ...runtimeSubjectContext,
      workspace_mode: overrides.workspace_mode ?? this.config.workspaceMode ?? null,
      interaction_mode: overrides.interaction_mode ?? this.config.interactionMode ?? null,
      flow_step: overrides.flow_step ?? this.config.flowStep ?? null,
      readiness_summary: overrides.readiness_summary ?? this.config.readinessSummary ?? null
    };
  }
  buildRequestContext(context = {}) {
    const explicitAssistantContext = context.assistant_context && typeof context.assistant_context === "object" && !Array.isArray(context.assistant_context) ? context.assistant_context : {};
    const runtimeSubjectContext = this.buildRuntimeSubjectContext({
      assistant_profile_id: context.assistant_profile_id ?? explicitAssistantContext.assistant_profile_id,
      runtime_agent_id: context.runtime_agent_id ?? explicitAssistantContext.runtime_agent_id,
      subject_agent_id: context.subject_agent_id ?? explicitAssistantContext.subject_agent_id,
      subject_context_key: context.subject_context_key ?? explicitAssistantContext.subject_context_key,
      onboarding_session_id: context.onboarding_session_id ?? explicitAssistantContext.onboarding_session_id,
      scan_id: context.scan_id ?? explicitAssistantContext.scan_id
    });
    const assistantContext = this.buildAssistantContextEnvelope({
      ...explicitAssistantContext,
      ...runtimeSubjectContext,
      agent_id: explicitAssistantContext.agent_id ?? context.agentId ?? this.config.agentId,
      workspace_mode: context.workspace_mode ?? explicitAssistantContext.workspace_mode,
      interaction_mode: context.interaction_mode ?? explicitAssistantContext.interaction_mode,
      flow_step: context.flow_step ?? explicitAssistantContext.flow_step,
      readiness_summary: context.readiness_summary ?? explicitAssistantContext.readiness_summary
    });
    return {
      ...context,
      ...runtimeSubjectContext,
      agentId: context.agentId ?? this.config.agentId,
      assistant_context: assistantContext,
      workspace_mode: context.workspace_mode ?? assistantContext.workspace_mode,
      interaction_mode: context.interaction_mode ?? assistantContext.interaction_mode,
      flow_step: context.flow_step ?? assistantContext.flow_step,
      readiness_summary: context.readiness_summary ?? assistantContext.readiness_summary
    };
  }
  resolveRequestContext() {
    const candidate = typeof this.config.requestContext === "function" ? this.config.requestContext() : this.config.requestContext;
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
      return {};
    }
    return candidate;
  }
  resolveTurnPromptOverride() {
    const candidate = typeof this.config.turnPromptOverride === "function" ? this.config.turnPromptOverride() : this.config.turnPromptOverride;
    if (typeof candidate !== "string") {
      return void 0;
    }
    const trimmed = candidate.trim();
    return trimmed || void 0;
  }
  composeTurnPromptOverride(sessionTurnPrompt) {
    const parts = [
      this.resolveTurnPromptOverride(),
      typeof sessionTurnPrompt === "string" ? sessionTurnPrompt.trim() : void 0
    ].filter((value) => Boolean(value));
    if (!parts.length) {
      return void 0;
    }
    return Array.from(new Set(parts)).join("\n\n");
  }
  /**
   * Send a user message and start streaming response.
   * Persisted outbox: write before sending; mark failed on error; clear on stream_end.
   */
  async sendMessage(conversationId, content, context = {}) {
    let targetId = conversationId;
    if (!targetId) {
      targetId = createId();
      this.store.getState().initSession(targetId);
      this.store.getState().setActiveConversationId(targetId);
    }
    const state = this.store.getState();
    const session = state.sessions[targetId];
    const draftAttachments = session ? session.draftAttachments : [];
    const uploadedAttachments = getUploadedDraftAttachments(draftAttachments);
    const fileIds = uploadedAttachmentsToFileIds(uploadedAttachments);
    state.setOutbox?.(targetId, { content, fileIds, createdAt: Date.now(), status: "pending" });
    const userMsgId = state.addMessage(targetId, "user", content);
    const assistantMsgId = state.addMessage(targetId, "assistant", "");
    state.setSessionStatus(targetId, "CONNECTING");
    if (uploadedAttachments.length > 0) {
      const sess = state.sessions[targetId];
      const msg = sess.messages.find((m) => m.id === userMsgId);
      if (msg) {
        const existingParts = Array.isArray(msg.parts) ? msg.parts.slice() : [];
        msg.parts = addFileBubblesToUserMessageParts({
          existingParts,
          userMsgId,
          uploadedAttachments
        });
      }
    }
    state.clearDraft(targetId);
    const runOverrides = state.sessions[targetId]?.runOverrides || {};
    const explicitContext = context && typeof context === "object" && !Array.isArray(context) ? context : {};
    const runtimeRequestContext = this.resolveRequestContext();
    const mergedRequestContext = {
      ...runtimeRequestContext,
      ...explicitContext
    };
    const turnPromptOverride = this.composeTurnPromptOverride(runOverrides.extra_system_prompt);
    const parts = [];
    if (content) parts.push({ type: "text", content });
    parts.push(...attachmentsToRequestParts(uploadedAttachments));
    const payload = {
      conversation_id: targetId,
      user_message: content,
      conversationId: targetId,
      content,
      parts,
      context: this.buildRequestContext(mergedRequestContext),
      tool_results: mergedRequestContext.tool_results,
      turn_prompt_override: turnPromptOverride,
      run_overrides: Object.keys(runOverrides).length > 0 ? runOverrides : void 0
    };
    const endpointOverride = this.config.useV2Engine ? `${this.config.apiBaseUrl}/api/v2/chat/stream` : void 0;
    try {
      await this.streamHandler.connect(targetId, payload, assistantMsgId, endpointOverride);
    } catch (e) {
      console.error("Failed to start stream", e);
      state.setSessionStatus(targetId, "ERROR", "Failed to send message");
      state.markOutboxFailed?.(targetId);
    }
    return targetId;
  }
  async resendPending(conversationId) {
    const st = this.store.getState();
    const cid = conversationId || st.activeConversationId;
    if (!cid) return;
    const outbox = st.sessions[cid]?.outbox;
    if (!outbox) return;
    outbox.fileIds.forEach((fid) => {
      st.addAttachment(cid, {
        clientId: createId(),
        fileId: fid,
        url: filesService3.getDownloadUrl(fid),
        name: `file-${fid.slice(0, 6)}`,
        type: "file",
        status: "uploaded"
      });
    });
    await this.sendMessage(cid, outbox.content);
  }
  async submitToolResult(callId, result, name) {
    return submitToolResultWithGate({
      store: this.store,
      allowClientTools: !!this.config.allowClientTools,
      sendMessage: this.sendMessage.bind(this),
      callId,
      result,
      name
    });
  }
  async getConversation(id) {
    return this.conversationManager.loadConversation(id);
  }
  async listConversations() {
    return this.conversationManager.listConversations();
  }
  async getAttachmentsSummary(conversationId) {
    return conversationService3.getAttachments(conversationId);
  }
  async listKnowledgeProjects() {
    return fsService.listProjects();
  }
  async getKnowledgeProjectTree(projectId) {
    return fsService.getProjectTree(projectId);
  }
  async saveFsFile(payload) {
    return fsService.saveFile(payload);
  }
  async linkUploadedFileToKB(payload) {
    return fsService.linkUploadedFileToKB(payload);
  }
  async listFileGroups() {
    return fileGroupsService.listGroups();
  }
  async getFileGroupItems(groupId) {
    return fileGroupsService.getItems(groupId);
  }
  async listLibraryFiles(params) {
    return filesLibraryService.list(params);
  }
  getActiveConversationId() {
    return this.store.getState().activeConversationId;
  }
  getSessionMessages(conversationId) {
    return this.store.getState().sessions[conversationId]?.messages || [];
  }
  getScrollPosition(conversationId) {
    return this.store.getState().sessions[conversationId]?.scrollTop ?? 0;
  }
  setScrollPosition(conversationId, scrollTop) {
    this.store.getState().setScrollPosition(conversationId, scrollTop);
  }
  getScrollAnchor(conversationId) {
    return this.store.getState().sessions[conversationId]?.scrollAnchor ?? null;
  }
  setScrollAnchor(conversationId, anchor) {
    this.store.getState().setScrollAnchor(conversationId, anchor);
  }
  async editMessage(messageId, newContent) {
    const currentConversationId = this.store.getState().activeConversationId;
    if (!currentConversationId) throw new Error("Cannot edit message: No active conversation.");
    return this.conversationManager.branchAndEdit(currentConversationId, messageId, newContent);
  }
  async revertToVersion(versionId) {
    return this.conversationManager.revertToVersion(versionId);
  }
  async forkFromVersion(versionId) {
    return this.conversationManager.forkFromVersion(versionId);
  }
  deleteMessage(messageId) {
    this.store.getState().deleteMessage(messageId);
  }
  async deleteConversation(id) {
    return this.conversationManager.deleteConversation(id);
  }
  async renameConversation(id, title) {
    return this.conversationManager.renameConversation(id, title);
  }
  async removeFileFromConversation(conversationId, messageId, opts) {
    if (!conversationId) return false;
    const s = this.store.getState();
    const session = s.sessions[conversationId];
    if (!session) return false;
    const msgIndex = session.messages.findIndex((m) => m.id === messageId);
    if (msgIndex < 0) return false;
    const res = await conversationService3.getConversationById(conversationId, true);
    if (res?.isError) return false;
    const payload = res;
    const messages = Array.isArray(payload.messages) ? payload.messages : [];
    const target = messages[msgIndex];
    if (!target) return false;
    const updated = (() => {
      const newMsg = { ...target, parts: Array.isArray(target.parts) ? target.parts.map((p) => ({ ...p })) : [] };
      const newParts = [];
      for (const part of newMsg.parts) {
        if (part.part_kind === "document-url" || part.part_kind === "image-url") {
          const pid = parseFileIdFromUrl(part.url);
          const isMatch = opts.fileId && pid === opts.fileId || opts.url && part.url === opts.url;
          if (isMatch) continue;
        }
        let changed = false;
        if (Array.isArray(part.content)) {
          const newContent = [];
          for (let i = 0; i < part.content.length; i++) {
            const item = part.content[i];
            if (item && typeof item === "object") {
              const isFile = item.kind === "document-url" || item.kind === "image-url" || !!item.url;
              if (isFile) {
                const pid = parseFileIdFromUrl(item.url);
                const isMatch = opts.fileId && pid === opts.fileId || opts.url && item.url === opts.url;
                if (isMatch) {
                  const prev = part.content[i - 1];
                  const prevIsLabel = typeof prev === "string" && (prev.trim().toLowerCase().startsWith("file: ") || prev.trim().toLowerCase().startsWith("image: "));
                  if (prevIsLabel && newContent.length > 0 && typeof newContent[newContent.length - 1] === "string") {
                    newContent.pop();
                  }
                  changed = true;
                  continue;
                }
              }
            }
            newContent.push(item);
          }
          if (changed) {
            if (newContent.length === 0) continue;
            newParts.push({ ...part, content: newContent });
            continue;
          }
        }
        newParts.push(part);
      }
      return { ...newMsg, parts: newParts };
    })();
    const operations = [{ op: "replace", index: msgIndex, message: updated }];
    const patchRes = await conversationService3.patchConversationMessages(conversationId, operations);
    if (patchRes?.isError) return false;
    await this.conversationManager.loadConversation(conversationId);
    return true;
  }
  async addFilesToMessage(conversationId, messageId, items) {
    if (!conversationId || !messageId || !items.length) return false;
    const res = await conversationService3.getConversationById(conversationId, true);
    if (res?.isError) return false;
    const payload = res;
    const messages = Array.isArray(payload.messages) ? payload.messages : [];
    const msgIndex = messages.findIndex((m) => m.id === messageId);
    if (msgIndex < 0) return false;
    const target = messages[msgIndex];
    const newMsg = { ...target, parts: Array.isArray(target.parts) ? target.parts.map((p) => ({ ...p })) : [] };
    let partIdx = newMsg.parts.findIndex((p) => Array.isArray(p.content));
    if (partIdx < 0) {
      newMsg.parts.push({ part_kind: "llm-response", content: [] });
      partIdx = newMsg.parts.length - 1;
    }
    const pushTo = newMsg.parts[partIdx];
    const contentArr = Array.isArray(pushTo.content) ? pushTo.content : [];
    for (const it of items) {
      const url = filesService3.getDownloadUrl(it.fileId);
      const kind = (it.media_type || "").startsWith("image/") ? "image-url" : "document-url";
      contentArr.push({ kind, url, media_type: it.media_type || "application/octet-stream" });
    }
    pushTo.content = contentArr;
    const operations = [{ op: "replace", index: msgIndex, message: newMsg }];
    const patchRes = await conversationService3.patchConversationMessages(conversationId, operations);
    if (patchRes?.isError) return false;
    await this.conversationManager.loadConversation(conversationId);
    return true;
  }
  async attachGroupToMessage(conversationId, messageId, groupId) {
    try {
      const res = await fileGroupsService.resolve(groupId);
      if (res?.isError) return false;
      const files = res?.files || [];
      const items = files.filter((f) => !!f.file_id).map((f) => ({ fileId: f.file_id }));
      if (items.length === 0) return true;
      return this.addFilesToMessage(conversationId, messageId, items);
    } catch (e) {
      console.error("[ChatClient] attachGroupToMessage failed", e);
      return false;
    }
  }
  async shareConversation(conversationId) {
    const headers = await this.config.authProvider.getHeaders();
    const res = await fetch(`${this.config.apiBaseUrl}/api/v1/conversations/${conversationId}/share`, {
      method: "POST",
      headers
    });
    if (!res.ok) throw new Error("Share failed");
    const data = await res.json();
    return data.share_url;
  }
};

// src/auth/WidgetAuthProvider.ts
var WidgetAuthProvider = class {
  constructor(widgetId, initialToken) {
    this.token = null;
    this.storage = getSafeSessionStorage();
    this.widgetId = widgetId;
    this.token = initialToken || null;
  }
  storageKey() {
    return `dopomogai:wat:widget:${this.widgetId}`;
  }
  legacyStorageKey() {
    return `banty:wat:${this.widgetId}`;
  }
  async getToken() {
    if (this.token) return this.token;
    if (this.storage) {
      const stored = this.storage.getItem(this.storageKey());
      if (stored) {
        this.token = stored;
        return stored;
      }
      const legacy = this.storage.getItem(this.legacyStorageKey());
      if (legacy) {
        this.token = legacy;
        try {
          this.storage.setItem(this.storageKey(), legacy);
        } catch {
        }
        return legacy;
      }
    }
    throw new Error("WidgetAuthProvider: No token available.");
  }
  async getHeaders() {
    const t = await this.getToken();
    return {
      Authorization: `Bearer ${t}`,
      "x-wat": t
    };
  }
  onTokenRefresh(newToken) {
    this.token = newToken;
    if (this.storage) {
      try {
        this.storage.setItem(this.storageKey(), newToken);
        this.storage.setItem(this.legacyStorageKey(), newToken);
      } catch {
      }
    }
  }
};

// src/audio/recorder.ts
var AudioRecorder = class {
  constructor() {
    this.mediaRecorder = null;
    this.chunks = [];
  }
  async start() {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      throw new Error("AudioRecorder: mediaDevices unavailable.");
    }
    if (typeof MediaRecorder === "undefined") {
      throw new Error("AudioRecorder: MediaRecorder not supported.");
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.mediaRecorder = new MediaRecorder(stream);
    this.chunks = [];
    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.mediaRecorder.start();
  }
  async stop() {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) return reject(new Error("Recorder not initialized."));
      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: "audio/webm" });
        this.chunks = [];
        this.mediaRecorder?.stream.getTracks().forEach((track) => track.stop());
        this.mediaRecorder = null;
        resolve(blob);
      };
      this.mediaRecorder.stop();
    });
  }
};

// src/audio/player.ts
var AudioStreamPlayer = class {
  constructor() {
    this.ctx = null;
    this.nextStartTime = 0;
  }
  async playChunk(base64Data) {
    if (typeof window === "undefined") {
      throw new Error("AudioStreamPlayer: window is not available.");
    }
    if (!this.ctx) {
      const AudioContextImpl = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextImpl) throw new Error("AudioStreamPlayer: AudioContext not supported.");
      this.ctx = new AudioContextImpl();
    }
    const binaryString = window.atob(base64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const buffer = await this.ctx.decodeAudioData(bytes.buffer);
    this.scheduleBuffer(buffer);
  }
  scheduleBuffer(buffer) {
    if (!this.ctx) return;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.ctx.destination);
    const now = this.ctx.currentTime;
    const start = Math.max(now, this.nextStartTime);
    source.start(start);
    this.nextStartTime = start + buffer.duration;
  }
  stop() {
    this.ctx?.close();
    this.ctx = null;
    this.nextStartTime = 0;
  }
};

// src/auth/PlatformAuthProvider.ts
var PlatformAuthProvider = class {
  constructor(token) {
    this.token = token;
  }
  async getToken() {
    if (!this.token) {
      throw new Error("PlatformAuthProvider: Token is missing.");
    }
    return this.token;
  }
  async getHeaders() {
    const t = await this.getToken();
    return {
      Authorization: `Bearer ${t}`
    };
  }
  // Platform tokens are usually refreshed by the main app (SupabaseProvider), 
  // but we implement the method to satisfy the interface.
  onTokenRefresh(newToken) {
    this.token = newToken;
  }
};

// src/auth/VisitorWidgetAuthProvider.ts
var VisitorWidgetAuthProvider = class {
  constructor(widgetVersionId, initialToken) {
    this.token = null;
    this.storage = getSafeSessionStorage();
    this.widgetVersionId = widgetVersionId;
    this.token = initialToken || null;
  }
  storageKey() {
    return `dopomogai:wat:widget:${this.widgetVersionId}`;
  }
  async getToken() {
    if (this.token) return this.token;
    const stored = this.storage ? this.storage.getItem(this.storageKey()) : null;
    if (stored) {
      this.token = stored;
      return stored;
    }
    throw new Error("VisitorWidgetAuthProvider: No token available.");
  }
  async getHeaders() {
    const t = await this.getToken();
    return {
      Authorization: `Bearer ${t}`,
      "x-wat": t
    };
  }
  onTokenRefresh(newToken) {
    if (!newToken) return;
    this.token = newToken;
    if (this.storage) {
      try {
        this.storage.setItem(this.storageKey(), newToken);
      } catch {
      }
    }
  }
};
export {
  AudioRecorder,
  AudioStreamPlayer,
  ChatClient,
  DEFAULT_CHAT_PERSIST_KEY,
  PlatformAuthProvider,
  VisitorWidgetAuthProvider,
  WidgetAuthProvider,
  buildChatPersistKey,
  buildChatPersistNamespace,
  createChatStore
};
